/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import TicketService from "../../services/TicketService";
import AssetService from "../../services/AssetService";
import FacilityService from "../../services/FacilityService";
import UserService from "../../services/UserService";
import { If } from "../../utils/Controls";
import { AntdPaging, AntdSelect, AntdDatepicker } from "../../utils/Antd";
import util from "../../utils/util";
import moment from "moment";
import plus from "../assets/icons/plus.svg";
import edit from "../assets/icons/pen-to-square-solid.svg";
import search from "../assets/icons/magnifying-glass-solid.svg";

import './StatusColour.css';
import {
    Input,
    Button,
    message,
    Modal,
    Card,
    Statistic,
    Progress,
    Row,
    Col,
    Tabs,
    Table,
    Tag,
    Select,
    DatePicker,
    Tooltip,
    Alert
} from 'antd';
import {
    BarChartOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    ToolOutlined,
    TeamOutlined,
    CalendarOutlined,
    WarningOutlined,
    RiseOutlined,
    FallOutlined,
    LineChartOutlined
} from '@ant-design/icons';
import TicketDetail from './TicketDetail';

const { RangePicker } = DatePicker;
const { Option } = Select;

export default function Ticket() {
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [infrastuctureSubCats, setInfrastuctureSubCats] = useState([]);
    const [assets, setAssets] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [result, setResult] = useState({ data: [], page: {} });
    const [showAnalytics, setShowAnalytics] = useState(true);
    const [analyticsDateRange, setAnalyticsDateRange] = useState([null, null]);
    const [selectedAnalyticsType, setSelectedAnalyticsType] = useState('all');
    const sdataRef = useRef({ p: 1, ps: 25 });
    const formRef = useRef();
    const dtlRef = useRef({});

    const isNational = util.isNational();
    const modules = util.getModules();
    const ticketStatusNames = [
        { id: 1, name: 'Open' },
        { id: 2, name: 'On Hold - Awaiting Spares' },
        { id: 3, name: 'On Hold - Spares Supplied' },
        { id: 4, name: 'Completed' },
        { id: 5, name: 'Closed' }
    ];

    // Enhanced Analytics Calculations
    const analytics = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        const thisWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thisMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        const lastMonth = new Date(today.getTime() - 60 * 24 * 60 * 60 * 1000);

        // For analytics, use ALL data when national, filtered data when not national
        let baseTickets = result.data;
        
        // If national, don't filter by facility for analytics - show all data
        if (isNational === 1) {
            // For national users, analytics should show ALL facilities data
            baseTickets = result.data;
        }

        // Filter tickets based on analytics filters (date range and type only)
        const filteredTickets = baseTickets.filter(ticket => {
            const ticketDate = new Date(ticket.created);
            const matchesDateRange = !analyticsDateRange[0] || !analyticsDateRange[1] || 
                (ticketDate >= analyticsDateRange[0] && ticketDate <= analyticsDateRange[1]);
            const matchesType = selectedAnalyticsType === 'all' || ticket.type === selectedAnalyticsType;
            return matchesDateRange && matchesType;
        });

        // Basic KPIs
        const totalTickets = filteredTickets.length;
        const openTickets = filteredTickets.filter(t => t.status_name === 'Open').length;
        const completedTickets = filteredTickets.filter(t => t.status_name === 'Completed').length;
        const closedTickets = filteredTickets.filter(t => t.status_name === 'Closed').length;
        const onHoldTickets = filteredTickets.filter(t => 
            t.status_name.includes('On Hold')).length;

        // Completion Rate
        const completionRate = totalTickets > 0 ? 
            ((completedTickets + closedTickets) / totalTickets * 100) : 0;

        // Time-based metrics
        const todayTickets = filteredTickets.filter(t => 
            new Date(t.created) >= today).length;
        const yesterdayTickets = filteredTickets.filter(t => {
            const date = new Date(t.created);
            return date >= yesterday && date < today;
        }).length;
        const weekTickets = filteredTickets.filter(t => 
            new Date(t.created) >= thisWeek).length;
        const monthTickets = filteredTickets.filter(t => 
            new Date(t.created) >= thisMonth).length;
        const lastMonthTickets = filteredTickets.filter(t => {
            const date = new Date(t.created);
            return date >= lastMonth && date < thisMonth;
        }).length;

        // Growth calculations
        const dailyGrowth = yesterdayTickets > 0 ? 
            ((todayTickets - yesterdayTickets) / yesterdayTickets * 100) : 0;
        const monthlyGrowth = lastMonthTickets > 0 ? 
            ((monthTickets - lastMonthTickets) / lastMonthTickets * 100) : 0;

        // Status Distribution
        const statusDistribution = ticketStatusNames.map(status => ({
            status: status.name,
            count: filteredTickets.filter(t => t.status_name === status.name).length,
            percentage: totalTickets > 0 ? 
                (filteredTickets.filter(t => t.status_name === status.name).length / totalTickets * 100) : 0
        }));

        // Type Distribution
        const typeDistribution = ['Med/NonMed', 'Infrastructure'].map(type => ({
            type,
            count: filteredTickets.filter(t => t.type === type).length,
            percentage: totalTickets > 0 ? 
                (filteredTickets.filter(t => t.type === type).length / totalTickets * 100) : 0
        }));

        // Facility Performance (if national)
        const facilityPerformance = isNational ? 
            clients.map(client => {
                const clientTickets = filteredTickets.filter(t => t.client === client.name);
                const clientCompleted = clientTickets.filter(t => 
                    t.status_name === 'Completed' || t.status_name === 'Closed').length;
                return {
                    facility: client.name,
                    total: clientTickets.length,
                    completed: clientCompleted,
                    completionRate: clientTickets.length > 0 ? 
                        (clientCompleted / clientTickets.length * 100) : 0,
                    open: clientTickets.filter(t => t.status_name === 'Open').length
                };
            }).filter(f => f.total > 0) : [];

        // User Performance - for national, show ALL users across all facilities
        const userPerformance = (isNational === 1 ? assignedUsers : assignedUsers).map(user => {
            const userTickets = filteredTickets.filter(t => t.assigned_to_name === user.name);
            const userCompleted = userTickets.filter(t => 
                t.status_name === 'Completed' || t.status_name === 'Closed').length;
            const userOpen = userTickets.filter(t => t.status_name === 'Open').length;
            const userOnHold = userTickets.filter(t => t.status_name.includes('On Hold')).length;
            
            // Calculate average resolution time for completed tickets
            const completedTicketsWithTime = userTickets.filter(t => 
                t.status_name === 'Completed' && t.updated_at);
            const avgResolutionTime = completedTicketsWithTime.length > 0 ?
                completedTicketsWithTime.reduce((acc, ticket) => {
                    const created = new Date(ticket.created);
                    const completed = new Date(ticket.updated_at);
                    return acc + (completed - created) / (1000 * 60 * 60 * 24); // days
                }, 0) / completedTicketsWithTime.length : 0;

            return {
                user: user.name,
                role: user.role,
                facility: isNational === 1 ? (clients.find(c => c.id === user.client_id)?.name || 'Unknown') : null,
                total: userTickets.length,
                open: userOpen,
                onHold: userOnHold,
                completed: userCompleted,
                completionRate: userTickets.length > 0 ? 
                    (userCompleted / userTickets.length * 100) : 0,
                avgResolutionTime
            };
        }).filter(u => u.total > 0);

        // Asset/Infrastructure Performance
        const assetPerformance = filteredTickets.reduce((acc, ticket) => {
            const key = ticket.type === 'Med/NonMed' ? 
                ticket.item_full_name || 'Unknown Asset' : 
                ticket.infrastucture_type || 'Unknown Infrastructure';
            
            if (!acc[key]) {
                acc[key] = {
                    name: key,
                    type: ticket.type,
                    total: 0,
                    open: 0,
                    completed: 0
                };
            }
            
            acc[key].total++;
            if (ticket.status_name === 'Open') acc[key].open++;
            if (ticket.status_name === 'Completed' || ticket.status_name === 'Closed') {
                acc[key].completed++;
            }
            
            return acc;
        }, {});

        const assetPerformanceArray = Object.values(assetPerformance).map(asset => ({
            ...asset,
            failureRate: asset.total > 0 ? (asset.total / totalTickets * 100) : 0,
            completionRate: asset.total > 0 ? (asset.completed / asset.total * 100) : 0
        })).sort((a, b) => b.total - a.total);

        // SLA Performance (assuming 24 hours for critical, 72 hours for normal)
        const slaBreaches = filteredTickets.filter(ticket => {
            if (ticket.status_name === 'Completed' || ticket.status_name === 'Closed') return false;
            
            const created = new Date(ticket.created);
            const hoursOpen = (now - created) / (1000 * 60 * 60);
            
            // Critical tickets (Med/NonMed) should be resolved within 24 hours
            if (ticket.type === 'Med/NonMed' && hoursOpen > 24) return true;
            // Infrastructure tickets within 72 hours
            if (ticket.type === 'Infrastructure' && hoursOpen > 72) return true;
            
            return false;
        }).length;

        const slaCompliance = totalTickets > 0 ? 
            ((totalTickets - slaBreaches) / totalTickets * 100) : 100;

        // Priority Analysis (based on age and type)
        const criticalTickets = filteredTickets.filter(ticket => {
            if (ticket.status_name === 'Completed' || ticket.status_name === 'Closed') return false;
            const hoursOpen = (now - new Date(ticket.created)) / (1000 * 60 * 60);
            return (ticket.type === 'Med/NonMed' && hoursOpen > 24) || 
                   (ticket.type === 'Infrastructure' && hoursOpen > 72);
        }).length;

        const urgentTickets = filteredTickets.filter(ticket => {
            if (ticket.status_name === 'Completed' || ticket.status_name === 'Closed') return false;
            const hoursOpen = (now - new Date(ticket.created)) / (1000 * 60 * 60);
            return (ticket.type === 'Med/NonMed' && hoursOpen > 12 && hoursOpen <= 24) || 
                   (ticket.type === 'Infrastructure' && hoursOpen > 48 && hoursOpen <= 72);
        }).length;

        return {
            totalTickets,
            openTickets,
            completedTickets,
            closedTickets,
            onHoldTickets,
            completionRate,
            todayTickets,
            yesterdayTickets,
            weekTickets,
            monthTickets,
            dailyGrowth,
            monthlyGrowth,
            statusDistribution,
            typeDistribution,
            facilityPerformance,
            userPerformance,
            assetPerformanceArray,
            slaBreaches,
            slaCompliance,
            criticalTickets,
            urgentTickets
        };
    }, [result.data, analyticsDateRange, selectedAnalyticsType, clients, assignedUsers, isNational]);

    function getStatusClass(status) {
        switch (status) {
            case 'Open':
                return 'status-open';
            case 'On Hold - Awaiting Spares':
                return 'status-on-hold-awaiting-spares';
            case 'On Hold - Spares Supplied':
                return 'status-on-hold-spares-supplied';
            case 'Completed':
                return 'status-completed';
            case 'Closed':
                return 'status-closed';
            default:
                return '';
        }
    }

    const getPriorityColor = (ticket) => {
        if (ticket.status_name === 'Completed' || ticket.status_name === 'Closed') return '#52c41a';
        
        const hoursOpen = (new Date() - new Date(ticket.created)) / (1000 * 60 * 60);
        
        if ((ticket.type === 'Med/NonMed' && hoursOpen > 24) || 
            (ticket.type === 'Infrastructure' && hoursOpen > 72)) {
            return '#ff4d4f'; // Critical
        }
        
        if ((ticket.type === 'Med/NonMed' && hoursOpen > 12) || 
            (ticket.type === 'Infrastructure' && hoursOpen > 48)) {
            return '#fa8c16'; // Urgent
        }
        
        return '#1890ff'; // Normal
    };

    const list = (p, ps, getAssignedToUsers) => {
        const ids = new URLSearchParams(window.location.search).get('ids');
        sdataRef.current.p = p || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        sdataRef.current.ids = ids || '';
        
        // For national users showing analytics, ensure we get ALL data
        if (isNational === 1 && showAnalytics) {
            // Remove facility filters for analytics to get all national data
            const originalClientId = sdataRef.current.client_id;
            sdataRef.current.client_id = null; // Remove client filter for analytics
            
            util.showLoader();
            TicketService.lists(sdataRef.current, getAssignedToUsers).then(({ data }) => {
                setResult({ ...data.result });
                if (data.assigned_users) {
                    setAssignedUsers(data.assigned_users);
                }
                // Restore original client_id for ticket list view
                sdataRef.current.client_id = originalClientId;
            }).catch(e => {
                message.error(e.message);
                sdataRef.current.client_id = originalClientId;
            }).finally(() => {
                util.hideLoader();
            });
        } else {
            util.showLoader();
            TicketService.lists(sdataRef.current, getAssignedToUsers).then(({ data }) => {
                setResult({ ...data.result });
                if (data.assigned_users) {
                    setAssignedUsers(data.assigned_users);
                }
            }).catch(e => {
                message.error(e.message);
            }).finally(() => {
                util.hideLoader();
            });
        }
    }

    useEffect(() => {
        list(0, 0, 'getAssignedToUsers');

        FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
            let allClients = res.data.result.data.map(v => { return { id: v.id, name: v.business_name, is_national: v.is_national, is_me: v.is_me } });
            setClients(allClients);
        });

        UserService.usersWhoCanFulfillJobCard({ status: 1 }).then(res => {
            let allUsers = res.data.result.data.map(v => { return { id: v.id, name: v.name, role: v.role } });
            setUsers(allUsers);
        });

        AssetService.infrastuctureSubCats({ status: 1 }).then(res => {
            setInfrastuctureSubCats(res.data.result);
        });

        AssetService.lists({ cat_type: 'Med/NonMed', my_only: 'Y' }).then(res => {
            setAssets([...res.data.result.data.map((v) => { return { id: v.id, 
                            name: ((v.serial_no ? (v.serial_no + ' - ') : '')
                            + v.item_full_name) + ' [' + v.cat + ']' } })]);});

        FacilityService.allStationPoints({ status: 1 }).then(res => {
            setStationPoints([...res.data.result.data]);
        });

        return () => { message.destroy() }
    }, []);

    const renderAnalyticsDashboard = () => (
        <div className="analytics-dashboard" style={{ marginBottom: 24, padding: 16, backgroundColor: '#fafafa', borderRadius: 6 }}>
            {/* Toggle Button */}
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button 
                    onClick={() => {
                        setShowAnalytics(!showAnalytics);
                        // When showing analytics and user is national, reload all data
                        if (!showAnalytics && isNational === 1) {
                            list(1, 25, 'getAssignedToUsers');
                        }
                    }}
                    icon={showAnalytics ? <FallOutlined /> : <BarChartOutlined />}
                >
                    {showAnalytics ? 'Show Analytics' : 'Hide Analytics'}
                </Button>
            </div>

            {showAnalytics && (
                <>
                    {/* Filter Controls */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={12}>
                            <RangePicker 
                                onChange={setAnalyticsDateRange}
                                style={{ width: '100%' }}
                                placeholder={['Start Date', 'End Date']}
                            />
                        </Col>
                        <Col span={12}>
                            <Select 
                                value={selectedAnalyticsType} 
                                onChange={setSelectedAnalyticsType}
                                style={{ width: '100%' }}
                            >
                                <Option value="all">All Types</Option>
                                <Option value="Med/NonMed">Med/NonMed</Option>
                                <Option value="Infrastructure">Infrastructure</Option>
                            </Select>
                        </Col>
                    </Row>

                    {/* Key Performance Indicators */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Total Tickets"
                                    value={analytics.totalTickets}
                                    prefix={<ToolOutlined />}
                                />
                                <div style={{ marginTop: 8 }}>
                                    <Tag color={analytics.dailyGrowth > 0 ? 'red' : 'green'}>
                                        {analytics.dailyGrowth > 0 ? <RiseOutlined /> : <FallOutlined />}
                                        {Math.abs(analytics.dailyGrowth).toFixed(1)}% vs yesterday
                                    </Tag>
                                </div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Open Tickets"
                                    value={analytics.openTickets}
                                    prefix={<ExclamationCircleOutlined />}
                                    valueStyle={{ color: '#cf1322' }}
                                />
                                <div style={{ marginTop: 8 }}>
                                    <Tag color="red">Critical: {analytics.criticalTickets}</Tag>
                                    <Tag color="orange">Urgent: {analytics.urgentTickets}</Tag>
                                </div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Completion Rate"
                                    value={analytics.completionRate}
                                    precision={1}
                                    suffix="%"
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: analytics.completionRate > 80 ? '#3f8600' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="SLA Compliance"
                                    value={analytics.slaCompliance}
                                    precision={1}
                                    suffix="%"
                                    prefix={<ClockCircleOutlined />}
                                    valueStyle={{ color: analytics.slaCompliance > 95 ? '#3f8600' : '#cf1322' }}
                                />
                                <div style={{ marginTop: 8 }}>
                                    <Tag color="red">Breaches: {analytics.slaBreaches}</Tag>
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* Status and Type Distribution */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={12}>
                            <Card title="Status Distribution" style={{ height: 300 }}>
                                {analytics.statusDistribution.map(status => (
                                    <div key={status.status} style={{ marginBottom: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span className={getStatusClass(status.status)}>{status.status}</span>
                                            <span>{status.count} ({status.percentage.toFixed(1)}%)</span>
                                        </div>
                                        <Progress 
                                            percent={status.percentage} 
                                            showInfo={false}
                                            strokeColor={
                                                status.status === 'Open' ? '#cf1322' :
                                                status.status.includes('On Hold') ? '#fa8c16' :
                                                status.status === 'Completed' ? '#3f8600' : '#52c41a'
                                            }
                                        />
                                    </div>
                                ))}
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card title="Type Distribution" style={{ height: 300 }}>
                                {analytics.typeDistribution.map(type => (
                                    <div key={type.type} style={{ marginBottom: 20 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{type.type}</span>
                                            <span>{type.count} ({type.percentage.toFixed(1)}%)</span>
                                        </div>
                                        <Progress 
                                            percent={type.percentage} 
                                            showInfo={false}
                                            strokeColor={type.type === 'Med/NonMed' ? '#1890ff' : '#52c41a'}
                                        />
                                    </div>
                                ))}
                                <Alert
                                    message="Monthly Growth"
                                    description={
                                        <div>
                                            <Tag color={analytics.monthlyGrowth > 0 ? 'red' : 'green'}>
                                                {analytics.monthlyGrowth > 0 ? '+' : ''}{analytics.monthlyGrowth.toFixed(1)}% vs last month
                                            </Tag>
                                        </div>
                                    }
                                    type="info"
                                    showIcon
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Performance Tables */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={24}>
                            <Card title="User Performance" style={{ marginBottom: 16 }}>
                                <Table
                                    dataSource={analytics.userPerformance}
                                    columns={[
                                        { title: 'User', dataIndex: 'user', key: 'user' },
                                        { title: 'Role', dataIndex: 'role', key: 'role' },
                                        ...(isNational === 1 ? [{ title: 'Facility', dataIndex: 'facility', key: 'facility' }] : []),
                                        { title: 'Total', dataIndex: 'total', key: 'total' },
                                        { title: 'Open', dataIndex: 'open', key: 'open' },
                                        { title: 'On Hold', dataIndex: 'onHold', key: 'onHold' },
                                        { title: 'Completed', dataIndex: 'completed', key: 'completed' },
                                        { 
                                            title: 'Completion Rate', 
                                            dataIndex: 'completionRate', 
                                            key: 'completionRate',
                                            render: (rate) => (
                                                <Tag color={rate > 80 ? 'green' : rate > 60 ? 'orange' : 'red'}>
                                                    {rate.toFixed(1)}%
                                                </Tag>
                                            )
                                        },
                                        { 
                                            title: 'Avg Resolution (days)', 
                                            dataIndex: 'avgResolutionTime', 
                                            key: 'avgResolutionTime',
                                            render: (time) => time > 0 ? time.toFixed(1) : 'N/A'
                                        }
                                    ]}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    rowKey="user"
                                />
                            </Card>
                        </Col>
                    </Row>

                    {isNational && analytics.facilityPerformance.length > 0 && (
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col span={24}>
                                <Card title="Facility Performance">
                                    <Table
                                        dataSource={analytics.facilityPerformance}
                                        columns={[
                                            { title: 'Facility', dataIndex: 'facility', key: 'facility' },
                                            { title: 'Total', dataIndex: 'total', key: 'total' },
                                            { title: 'Open', dataIndex: 'open', key: 'open' },
                                            { title: 'Completed', dataIndex: 'completed', key: 'completed' },
                                            { 
                                                title: 'Completion Rate', 
                                                dataIndex: 'completionRate', 
                                                key: 'completionRate',
                                                render: (rate) => (
                                                    <Tag color={rate > 80 ? 'green' : rate > 60 ? 'orange' : 'red'}>
                                                        {rate.toFixed(1)}%
                                                    </Tag>
                                                )
                                            }
                                        ]}
                                        pagination={false}
                                        size="small"
                                        rowKey="facility"
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={16}>
                        <Col span={24}>
                            <Card title="Asset/Infrastructure Performance">
                                <Table
                                    dataSource={analytics.assetPerformanceArray.slice(0, 10)}
                                    columns={[
                                        { title: 'Asset/Infrastructure', dataIndex: 'name', key: 'name' },
                                        { title: 'Type', dataIndex: 'type', key: 'type' },
                                        { title: 'Total Issues', dataIndex: 'total', key: 'total' },
                                        { title: 'Open', dataIndex: 'open', key: 'open' },
                                        { title: 'Completed', dataIndex: 'completed', key: 'completed' },
                                        { 
                                            title: 'Failure Rate', 
                                            dataIndex: 'failureRate', 
                                            key: 'failureRate',
                                            render: (rate) => (
                                                <Tag color={rate > 20 ? 'red' : rate > 10 ? 'orange' : 'green'}>
                                                    {rate.toFixed(1)}%
                                                </Tag>
                                            )
                                        },
                                        { 
                                            title: 'Completion Rate', 
                                            dataIndex: 'completionRate', 
                                            key: 'completionRate',
                                            render: (rate) => `${rate.toFixed(1)}%`
                                        }
                                    ]}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    rowKey="name"
                                />
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );

    const renderTicketList = () => (
        <div>
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Breakdown Reporting</h2>
                </div>
                <div className="my-auto ml-auto">
                    {modules['add_ticket'] === 1 &&
                        <Button type="primary" onClick={() => formRef.current.openForm()}>
                            <img src={plus} alt='' className='mr5' height={15}/>
                            Add New
                        </Button>
                    }
                </div>
            </div>

            {/* Analytics Dashboard */}
            {renderAnalyticsDashboard()}

            <div className="page-pad">
                {result.data.length > 0 &&
                    <div className="text-secondary mb8">
                        Showing {result.page.start + 1} - {result.page.start + result.page.total} of {result.page.total_records} records.
                    </div>
                }

                <div className="tbl-search-head">
                    <SearchForm
                        dataRef={sdataRef}
                        onSearch={list}
                        clients={clients}
                        ticketStatusNames={ticketStatusNames}
                        assignedUsers={assignedUsers}
                        isNational={isNational}
                    />
                </div>

                <If cond={result.data.length}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm font-xs table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                                <tr>
                                    <th className="w20">SN</th>
                                    {isNational === 1 && <th className="w100">Facility</th>}
                                    <th className="w80 nowrap">Job No</th>
                                    <th className="w100">Type</th>
                                    <th>Subject</th>
                                    <th className="w150">Assigned To</th>
                                    <th className="w100">Created On</th>
                                    <th className="w80">Status</th>
                                    <th className="w60">Priority</th>
                                    {modules['add_ticket'] === 1 && <th className="w20"></th>}
                                </tr>
                            </thead>
                            <tbody className="table-text-top font-sm">
                                {result.data.map((v, i) => (
                                    <tr key={i} style={{ borderLeft: `4px solid ${getPriorityColor(v)}` }}>
                                        <td>{i + 1}.</td>
                                        {isNational === 1 && <td>{v.client}</td>}
                                        <td>
                                            <div
                                                className="ticket-btn"
                                                onClick={() => dtlRef.current.openDetail(v.id)}
                                            >
                                                {v.ticket_no}
                                            </div>
                                        </td>
                                        <td>{v.type}</td>
                                        <td>
                                            <div className="uc bold600">{v.subject}</div>
                                            <div className="pt2 text-secondary fs11">
                                                {v.type === 'Med/NonMed' ? (
                                                    <div>
                                                        {v.serial_no ? v.serial_no + ' - ' : ''} {v.item_full_name} [{v.cat}]
                                                    </div>
                                                ) : (
                                                    <div>Infrastructure Type: <span className="bold600">{v.infrastucture_type}</span></div>
                                                )}
                                            </div>
                                            <div className="pt3 text-secondary fs11">
                                                Created By: <span className="bold600">{v.created_by_name} [{v.created_by_role}]</span>
                                            </div>
                                        </td>

                                        <td>{v.assigned_to_name || 'N/A'}  [{v.assigned_to_role}]</td>

                                        <td>
                                            <div className="nowrap">{util.getDate(v.created, 'DD MMM YYYY @ hh:mm A')}</div>
                                            <div className="pt2 text-secondary fs11">
                                                {(() => {
                                                    const hoursOpen = (new Date() - new Date(v.created)) / (1000 * 60 * 60);
                                                    if (hoursOpen < 24) return `${Math.floor(hoursOpen)}h ago`;
                                                    return `${Math.floor(hoursOpen / 24)}d ago`;
                                                })()}
                                            </div>
                                        </td>

                                        <td className={`nowrap uc ${getStatusClass(v.status_name)}`}>{v.status_name}</td>

                                        <td>
                                            {(() => {
                                                const hoursOpen = (new Date() - new Date(v.created)) / (1000 * 60 * 60);
                                                let priority = 'Normal';
                                                let color = '#1890ff';
                                                
                                                if (v.status_name === 'Completed' || v.status_name === 'Closed') {
                                                    priority = 'Resolved';
                                                    color = '#52c41a';
                                                } else if ((v.type === 'Med/NonMed' && hoursOpen > 24) || 
                                                          (v.type === 'Infrastructure' && hoursOpen > 72)) {
                                                    priority = 'Critical';
                                                    color = '#ff4d4f';
                                                } else if ((v.type === 'Med/NonMed' && hoursOpen > 12) || 
                                                          (v.type === 'Infrastructure' && hoursOpen > 48)) {
                                                    priority = 'Urgent';
                                                    color = '#fa8c16';
                                                }
                                                
                                                return <Tag color={color}>{priority}</Tag>;
                                            })()}
                                        </td>

                                        {modules['add_ticket'] === 1 &&
                                            <td className="text-center">
                                                <Button.Group size="small">
                                                    <Button type="default" onClick={() => formRef.current.openForm(v)} disabled={!v.edit_allowed}>
                                                        <img src={edit} alt='' height={15}/>
                                                    </Button>
                                                </Button.Group>
                                            </td>
                                        }
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex tbl-foot-bx">
                        <AntdPaging
                            onChange={list}
                            total={result.page.total_records}
                            current={result.page.cur_page}
                            pageSize={sdataRef.current.ps}
                            showSizeChanger
                        />
                    </div>
                </If>
                <If cond={!result.data.length}>
                    <div className="no-rec">No record found</div>
                </If>
            </div>
        </div>
    );

    return (
        <div className="page-content">
            {renderTicketList()}

            <AddForm
                ref={formRef}
                callback={list}
                pageno={sdataRef.current.p}
                assets={assets}
                infrastuctureSubCats={infrastuctureSubCats}
                stationPoints={stationPoints}
                isNational={isNational}
            />

            <TicketDetail
                refOb={dtlRef}
                users={users}
                ticketStatusNames={ticketStatusNames}
                callback={list}
                pageno={sdataRef.current.p}
                stationPoints={stationPoints}
            />
        </div>
    )
}

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, ticketStatusNames, isNational } = props;
    let [data, setData] = useState({ ...dataRef.current });
    const [assignedUsers, setAssignedUsers] = useState([]);

    const handleChange = (v, k) => {
        data[k] = v;
        if (k === 'client_id') {
            data.assigned_to = null;
        }
        setData({ ...data });
    }
    useEffect(() => {
        dataRef.current = { ...data };
    }, [data]);

    useEffect(() => {
        let ob = clients.find(v => v.is_me);
        setData({ ...data, client_id: ob?.id });
    }, [clients]);

    useEffect(() => {
        setAssignedUsers(props.assignedUsers);
    }, [props.assignedUsers]);

    useEffect(() => {
        setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
    }, [dataRef.current.p, dataRef.current.ps]);

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                {isNational === 1 &&
                    <div className="w150 mr5">
                        <div className="text-secondary fs11 mb2">Facility</div>
                        <AntdSelect
                            placeholder="Facility (All)"
                            showSearch
                            options={clients}
                            value={data.client_id}
                            onChange={v => handleChange(v, 'client_id')}
                        />
                    </div>
                }

                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">Assigned To</div>
                    <AntdSelect
                        placeholder="Assigned To (All)"
                        showSearch
                        allowClear
                        options={assignedUsers.filter(v => v.client_id === data.client_id)}
                        value={data.assigned_to}
                        onChange={v => handleChange(v, 'assigned_to')}
                    />
                </div>

                <div className="w150 mr5">
                    <div className="text-secondary fs11 mb2">Type</div>
                    <AntdSelect
                        placeholder="Type"
                        allowClear
                        options={[{ value: 'Med/NonMed', label: 'Med/NonMed' }, { value: 'Infrastructure', label: 'Infrastructure' }]}
                        value={data.type}
                        onChange={v => handleChange(v, 'type')}
                    />
                </div>

                <div className="w250 mr5">
                    <div className="text-secondary fs11 mb2">Status</div>
                    <AntdSelect
                        placeholder="Status (All)"
                        allowClear
                        showSearch
                        options={ticketStatusNames}
                        value={data.status_id}
                        onChange={v => handleChange(v, 'status_id')}
                    />
                </div>

                <div className="w130 mr5">
                    <div className="text-secondary fs11 mb2">From Date</div>
                    <AntdDatepicker
                        placeholder="From Date"
                        allowClear
                        value={data.from_date}
                        onChange={v => {
                            if (data.to_date && moment(v) > moment(data.to_date)) {
                                message.error("From date should be less than or equal to To Date");
                                v = null;
                            }
                            handleChange(v, 'from_date');
                        }}
                    />
                </div>

                <div className="w130 mr5">
                    <div className="text-secondary fs11 mb2">To Date</div>
                    <AntdDatepicker
                        placeholder="To Date"
                        allowClear
                        value={data.to_date}
                        onChange={v => {
                            if (data.from_date && moment(v) < moment(data.from_date)) {
                                message.error("To date should be greater than or equal to From Date");
                                v = null;
                            }
                            handleChange(v, 'to_date');
                        }}
                    />
                </div>

                <div className="w160 mr5">
                    <div className="text-secondary fs11 mb2">Job#</div>
                    <Input placeholder="Job Number" allowClear value={data.k} onChange={e => handleChange(e.target.value, 'k')} />
                </div>

                <div className="pt15">
                    <Button className="mt3" type="primary" onClick={() => onSearch()}><img src={search} alt='' height={15}/></Button>
                </div>
            </div>
        </form>
    )
}

const AddForm = forwardRef((props, ref) => {
    
    const { callback, pageno, assets, infrastuctureSubCats, stationPoints } = props;
    const [showForm, setShowForm] = useState(false);
    const [data, setData] = useState({});
    const [currentStationId, setCurrentStationId] = useState(null);
    const [filteredAssets, setFilteredAssets] = useState(assets);

    // Fetch assets based on station_id
    const fetchAssets = (stationId) => { 
        AssetService.getAssetsByStationId({ cat_type: 'Med/NonMed', my_only: 'Y', station_id: stationId })
            .then(res => {
                setFilteredAssets(res.data.result.data.map(v => ({
                    id: v.id,
                    name: ((v.serial_no ? (v.serial_no + ' - ') : '') + v.item_full_name) + ' [' + v.cat + ']'
                })));
            })
            .catch(err => {
                message.error(err.message);
            });
    };

    // Handle change in station point
    const handleStationChange = (value) => {
        setCurrentStationId(value);
        fetchAssets(value); // Fetch assets for the selected station point
        handleChange(value, 'station_id');
    };

   
    const handleChange=(v, k)=>{
        data[k]=v;
        setData({ ...data });
    }
    useEffect(() => {
        if (currentStationId) {
            fetchAssets(currentStationId);
        }
    }, [currentStationId]);
    
    const save = () => {
        message.destroy();
        util.showLoader();
        TicketService.save(data).then(({ data }) => {
            message.success(data.message || 'Saved');
            callback(data.id ? pageno : 1);
            setShowForm(false);
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }
    const closeForm = () => {
        setShowForm(false);
    }

    useImperativeHandle(ref, () => ({
        openForm(dtl) {
            setData(dtl ? { ...dtl } : { type: 'Med/NonMed' });
            setShowForm(true);
        }
    }));

    return (
        <Modal
            title={`${data.id ? 'Edit' : 'Create'} Job Card`}
            open={showForm}
            okText="Save"
            onOk={save}
            onCancel={closeForm}
            destroyOnClose
            maskClosable={false}
            width={800}
        >
            <form onSubmit={e => { e.preventDefault(); save() }} autoComplete="off" spellCheck="false">
                <div className="">
                    <div className="row mingap">
                        <div className="col-md-12 form-group">
                            <label className="req">Type</label>
                            <AntdSelect
                                options={[{ value: 'Med/NonMed', label: 'Med/NonMed' }, 
                                    { value: 'Infrastructure', label: 'Infrastructure' }]}
                                value={data.type}
                                onChange={v => handleChange(v, 'type')}
                            />
                        </div>

                        <div className="col-md-12 form-group">
                            <label className="req">Station Point</label>
                            <AntdSelect
                                showSearch
                                options={stationPoints.filter(v => v.is_my)}
                                value={data.station_id}
                                onChange={v => handleStationChange(v, 'station_id')}
                            />
                        </div>

                        {data.type === 'Med/NonMed' ? (
                            <div className="col-md-12 form-group">
                                <label className="req">Item_Name</label>
                                <AntdSelect 
                                showSearch 
                                options={filteredAssets} 
                                value={data.asset_id} 
                                onChange={v => handleChange(v, 'asset_id')} />
                            </div>
                        ) : (
                            <div className="col-md-12 form-group">
                                <label className="req">Infrastructure type</label>
                                <AntdSelect showSearch options={infrastuctureSubCats} value={data.asset_sub_cat_id} onChange={v => handleChange(v, 'asset_sub_cat_id')} />
                            </div>
                        )}

                        <div className="col-md-12 form-group">
                            <label className="req">Subject</label>
                            <Input value={data.subject} onChange={e => handleChange(e.target.value, 'subject')} />
                        </div>

                        <div className="col-md-12 form-group">
                            <label className="">Description</label>
                            <Input.TextArea rows="6" value={data.description} onChange={e => handleChange(e.target.value, 'description')} />
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    )
})